var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ReadOnlyField } from "@common/components/ReadOnlyField";
import Alert from "@iscr/views/NUI/Alert";
import { AsyncButton } from "@iscr/views/UIelems/AsyncButton";
import API_service from "@iscr/xAppLib/providers/API_service";
import AddressSearchNew from "@iscr/xAppLib/UIelems/AddressSearchNew";
import { useTRPC } from "@iscr/xAppLib/context/QueryProvider";
import { useMutation } from "@tanstack/react-query";
import { useContext, useEffect, useRef, useState } from "react";
import { Button, Checkbox, Form, Icon, Input, Loader } from "semantic-ui-react";
import { formatBsb } from "./model";
import { SapSyncSection } from "./SapSyncSection";
import { ProfileSyncActions } from "./ProfileSyncActions";
import { SapSyncContext } from "./SapStatusContext";
const EMPTY_FORM_STATE = {
    account_name: "",
    account_number: "",
    bsb: "",
    gst_registered: false,
    abn: "",
    type: "",
    entity: "",
    full_address: { formatted: "" },
};
const EMPTY_CHECK = { value: "", checking: false };
/** Verifies a field's value on blur (skipping empty/unchanged values) and tracks its check state. */
export function useVerifiedField(verify, unavailableResult) {
    const [check, setCheck] = useState(EMPTY_CHECK);
    // The value of the most recently *started* check - if the doctor edits and blurs again before an
    // in-flight check resolves, its (possibly out-of-order) response must not clobber the newer one.
    const latestValue = useRef("");
    const blur = (value) => __awaiter(this, void 0, void 0, function* () {
        if (!value || value === check.value)
            return undefined;
        latestValue.current = value;
        setCheck({ value, checking: true });
        try {
            const result = yield verify(value);
            if (latestValue.current !== value)
                return undefined;
            setCheck({ value, checking: false, result });
            return result;
        }
        catch (_a) {
            if (latestValue.current !== value)
                return undefined;
            setCheck({ value, checking: false, result: unavailableResult });
            return unavailableResult;
        }
    });
    const reset = () => {
        latestValue.current = "";
        setCheck(EMPTY_CHECK);
    };
    return { check, blur, reset };
}
/**
 * Derives checking/result/blocksSave for the *current* form value - a stale check left over from before
 * the doctor edited the field again is treated the same as no check at all.
 */
function fieldVerificationStatus(check, value, changedSinceLoad) {
    const fresh = check.value === value;
    const checking = fresh && check.checking;
    const result = fresh && !check.checking ? check.result : undefined;
    const blocksSave = (result === null || result === void 0 ? void 0 : result.status) === "invalid" || (changedSinceLoad && value !== "" && (checking || !result));
    return { checking, result, blocksSave };
}
const ValidationStatus = ({ checking, result, validLabel, }) => {
    if (checking) {
        return (_jsxs("span", { className: "mt-1 flex items-center gap-1 text-xs text-is-gray-25", children: [_jsx(Loader, { active: true, inline: true, size: "mini" }), " Checking..."] }));
    }
    if (!result)
        return null;
    if (result.status === "valid") {
        return (_jsxs("span", { className: "mt-1 flex items-center gap-1 text-xs text-green-600", children: [_jsx(Icon, { name: "check circle" }), " ", validLabel] }));
    }
    if (result.status === "invalid") {
        return (_jsxs("span", { className: "mt-1 flex items-center gap-1 text-xs text-red-600", children: [_jsx(Icon, { name: "times circle" }), " ", result.message || "Invalid"] }));
    }
    // "unavailable" - we couldn't check it (e.g. ABN Lookup GUID not yet configured, or a transient
    // network issue) - surfaced so it's not mistaken for a confirmed-valid value, but shouldn't block saving.
    return (_jsxs("span", { className: "mt-1 flex items-center gap-1 text-xs text-yellow-700", children: [_jsx(Icon, { name: "warning circle" }), " ", result.message || "Verification unavailable"] }));
};
export const ACCOUNT_NUMBER_MAX_LENGTH = 9;
/** Account numbers are stored without spaces - the doctor may type them grouped, e.g. "1234 5678". */
export const normalizeAccountNumber = (value) => value.replace(/\s/g, "");
/** Returns an error message if the account number isn't 1-9 digits (excluding spaces). This matches SAPs validation rule */
export function accountNumberError(value) {
    const digits = normalizeAccountNumber(value);
    if (!digits) {
        return "Enter an account number";
    }
    if (!/^\d+$/.test(digits)) {
        return "Account number can only contain digits (0-9)";
    }
    if (digits.length > ACCOUNT_NUMBER_MAX_LENGTH) {
        return `Account number can't be longer than ${ACCOUNT_NUMBER_MAX_LENGTH} digits`;
    }
    return undefined;
}
const DerivedFieldHint = () => (_jsx("span", { className: "mt-1 block text-xs text-is-gray-25", children: "Derived from ABN verification" }));
export const PaymentProfile = ({ doctor, loading, error, profile, onSaved, syncActions, editLabel = "Edit", updateLabel = "Update", docpayAccount, }) => {
    var _a, _b, _c, _d, _e;
    const trpc = useTRPC();
    const validateProfile = useMutation(trpc.docpay.profiles.validate.mutationOptions());
    const [form, setForm] = useState(EMPTY_FORM_STATE);
    const [initialForm, setInitialForm] = useState(EMPTY_FORM_STATE);
    const [saveError, setSaveError] = useState(null);
    // No existing profile means there's nothing to view read-only - start straight in edit mode.
    const [isEditing, setIsEditing] = useState(!profile);
    const hasLoadedProfile = useRef(false);
    const abnField = useVerifiedField((abn) => __awaiter(void 0, void 0, void 0, function* () { return (yield validateProfile.mutateAsync({ abn })).abn; }), { status: "unavailable", active: false, message: "ABN verification is temporarily unavailable." });
    const bsbField = useVerifiedField((bsb) => __awaiter(void 0, void 0, void 0, function* () { return (yield validateProfile.mutateAsync({ bsb })).bsb; }), { status: "unavailable", valid: false, message: "BSB validation unavailable, try again later." });
    useEffect(() => {
        if (profile) {
            const loaded = {
                account_name: profile.account_name || "",
                account_number: profile.account_number || "",
                bsb: formatBsb(profile.bsb || ""),
                gst_registered: profile.gst_registered || false,
                abn: profile.abn || "",
                type: profile.type || "",
                entity: profile.entity || "",
                full_address: profile.full_address || { formatted: "" },
            };
            setForm(loaded);
            setInitialForm(loaded);
            abnField.reset();
            bsbField.reset();
            // Only default to read-only the first time a profile loads, so a parent re-fetch
            // triggered elsewhere (e.g. refreshing a list) can't yank the doctor out of an
            // in-progress edit.
            if (!hasLoadedProfile.current) {
                setIsEditing(false);
                hasLoadedProfile.current = true;
            }
        }
    }, [profile]);
    const handleChange = (_, { name, value }) => {
        setForm(prev => (Object.assign(Object.assign({}, prev), { [name]: name === "bsb" ? formatBsb(value) : value })));
    };
    const handleCheckboxChange = (_, { name, checked }) => {
        setForm(prev => (Object.assign(Object.assign({}, prev), { [name]: checked !== null && checked !== void 0 ? checked : false })));
    };
    const handleAbnBlur = () => __awaiter(void 0, void 0, void 0, function* () {
        const abn = form.abn.trim();
        const result = yield abnField.blur(abn);
        // Only apply the auto-fill if the doctor hasn't already changed the ABN again while this was in flight.
        // The address is deliberately not derived from the ABN: the ABR only exposes the registered
        // entity's state/postcode, so it's captured through the address search instead.
        if ((result === null || result === void 0 ? void 0 : result.status) === "valid") {
            setForm(prev => {
                var _a, _b, _c;
                return prev.abn.trim() === abn
                    ? Object.assign(Object.assign({}, prev), { entity: (_a = result.entity) !== null && _a !== void 0 ? _a : prev.entity, type: (_b = result.type) !== null && _b !== void 0 ? _b : prev.type, gst_registered: (_c = result.registered) !== null && _c !== void 0 ? _c : prev.gst_registered }) : prev;
            });
        }
    });
    const handleBsbBlur = () => {
        const bsb = form.bsb.replace(/-/g, "");
        if (bsb.length === 6)
            bsbField.blur(bsb);
    };
    const isDirty = JSON.stringify(form) !== JSON.stringify(initialForm);
    // A field only needs to pass verification if the doctor has actually changed it this session -
    // pre-existing, unmodified values on an already-saved profile aren't forced through a live re-check.
    const abnValue = form.abn.trim();
    const bsbValue = form.bsb.replace(/-/g, "");
    const abnChangedSinceLoad = abnValue !== initialForm.abn.trim();
    const bsbChangedSinceLoad = bsbValue !== initialForm.bsb.replace(/-/g, "");
    const { checking: abnChecking, result: abnResult, blocksSave: abnBlocksSave, } = fieldVerificationStatus(abnField.check, abnValue, abnChangedSinceLoad);
    const { checking: bsbChecking, result: bsbResult, blocksSave: bsbBlocksSave, } = fieldVerificationStatus(bsbField.check, bsbValue, bsbChangedSinceLoad);
    // An empty field is already covered by the required-field check, so only flag a value that's been entered.
    const accountNumberValidationError = form.account_number ? accountNumberError(form.account_number) : undefined;
    const handleSave = () => __awaiter(void 0, void 0, void 0, function* () {
        setSaveError(null);
        const saved = Object.assign(Object.assign({}, form), { account_number: normalizeAccountNumber(form.account_number) });
        try {
            const res = yield API_service.load_data(`docpay/profiles?doc_id=${encodeURIComponent(doctor.doc_id)}${docpayAccount ? `&docpay_account=${docpayAccount}` : ""}`, Object.assign(Object.assign({}, saved), { bsb: saved.bsb.replace(/-/g, "") }));
            if (res === null || res === void 0 ? void 0 : res.err) {
                setSaveError(res.err);
            }
            else {
                setForm(saved);
                setInitialForm(saved);
                setIsEditing(false);
                onSaved === null || onSaved === void 0 ? void 0 : onSaved();
            }
        }
        catch (err) {
            setSaveError(String(err));
        }
    });
    const handleCancel = () => {
        setForm(initialForm);
        abnField.reset();
        bsbField.reset();
        setSaveError(null);
        setIsEditing(false);
    };
    // TODO(sap-sync-flag): remove this check when the SAP sync feature flag is removed - using this outside the provider context... immediate fix so the page goes live again but needs a refactor if we wanna see it in the profile, which i dont think we do, this can wait to be visible when we are officially live and not when we are uploading the profiles to SAP for testing
    const sapSyncEnabled = (_b = (_a = useContext(SapSyncContext)) === null || _a === void 0 ? void 0 : _a.isProfileSyncEnabledForDoc(doctor.doc_id)) !== null && _b !== void 0 ? _b : false;
    if (loading)
        return _jsx(Loader, { active: true, inline: "centered", className: "!mt-4" });
    if (error)
        return _jsx(Alert, { error: true, content: "Failed to load payment profile." });
    return (_jsxs(_Fragment, { children: [isEditing && (_jsx(Alert, { info: true, content: "Please ensure that your payment details are accurate to avoid any payment delays." })), sapSyncEnabled && doctor.sap_sync_state && (_jsx(SapSyncSection, { state: doctor.sap_sync_state, syncing: doctor.sap_syncing, sap_bp_id: doctor.sap_bp_id, sap_last_sync_error: doctor.sap_last_sync_error })), saveError && _jsx(Alert, { error: true, content: saveError, onDismiss: () => setSaveError(null) }), _jsx(Form, { children: isEditing ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "grid grid-cols-1 gap-x-6 gap-y-8 md:grid-cols-2", children: [_jsxs(Form.Field, { className: "!mb-0", children: [_jsx("label", { children: "Account Name" }), _jsx(Input, { name: "account_name", value: form.account_name, onChange: handleChange, placeholder: "Account Name" })] }), _jsxs(Form.Field, { className: "!mb-0", children: [_jsx("label", { children: "Account Number" }), _jsx(Input, { name: "account_number", value: form.account_number, onChange: handleChange, placeholder: "Account Number", error: !!accountNumberValidationError }), accountNumberValidationError && (_jsxs("span", { className: "mt-1 flex items-center gap-1 text-xs text-red-600", children: [_jsx(Icon, { name: "times circle" }), " ", accountNumberValidationError] }))] }), _jsxs(Form.Field, { className: "!mb-0", children: [_jsx("label", { children: "BSB" }), _jsx(Input, { name: "bsb", value: form.bsb, onChange: handleChange, onBlur: handleBsbBlur, placeholder: "###-###", loading: bsbChecking }), _jsx(ValidationStatus, { checking: bsbChecking, result: bsbResult, validLabel: (bsbResult === null || bsbResult === void 0 ? void 0 : bsbResult.status) === "valid" && bsbResult.record
                                                ? `Verified — ${bsbResult.record.bankName}, ${bsbResult.record.branchName}`
                                                : "Verified" })] }), _jsxs(Form.Field, { className: "!mb-0 self-end", children: [_jsx(Checkbox, { label: "GST Registered", name: "gst_registered", checked: form.gst_registered, onChange: handleCheckboxChange, disabled: true }), _jsx(DerivedFieldHint, {})] }), _jsxs(Form.Field, { className: "!mb-0", children: [_jsx("label", { children: "ABN" }), _jsx(Input, { name: "abn", value: form.abn, onChange: handleChange, onBlur: handleAbnBlur, placeholder: "## ### ### ###", loading: abnChecking }), _jsx(ValidationStatus, { checking: abnChecking, result: abnResult, validLabel: (abnResult === null || abnResult === void 0 ? void 0 : abnResult.status) === "valid" ? `Verified — ${abnResult.entity}` : "Verified" })] }), _jsxs(Form.Field, { className: "!mb-0", children: [_jsx("label", { children: "Type" }), _jsx(Input, { name: "type", value: form.type, placeholder: "(optional)", disabled: true }), _jsx(DerivedFieldHint, {})] }), _jsxs(Form.Field, { className: "!mb-0", children: [_jsx("label", { children: "Entity" }), _jsx(Input, { name: "entity", value: form.entity, placeholder: "(optional)", disabled: true }), _jsx(DerivedFieldHint, {})] }), _jsxs(Form.Field, { className: "!mb-0 md:col-span-2", children: [_jsx("label", { children: "Address" }), _jsx(AddressSearchNew
                                        // AddressSearchNew only reads its `value` prop once, on mount - PaymentProfile is remounted
                                        // (keyed by doctor) whenever the doctor changes, so this always mounts fresh with the
                                        // right address instead of showing whatever doctor's address loaded first.
                                        , { 
                                            // AddressSearchNew only reads its `value` prop once, on mount - PaymentProfile is remounted
                                            // (keyed by doctor) whenever the doctor changes, so this always mounts fresh with the
                                            // right address instead of showing whatever doctor's address loaded first.
                                            onChange: ({ formatted, address, state, postcode, suburb, }) => {
                                                setForm(prev => (Object.assign(Object.assign({}, prev), { full_address: { formatted, address, suburb, state, postcode } })));
                                            }, value: (_c = profile === null || profile === void 0 ? void 0 : profile.full_address) !== null && _c !== void 0 ? _c : {} })] })] }), _jsxs("div", { className: "sticky bottom-0 z-10 mt-6 flex flex-row justify-end gap-2 border-t border-border bg-white py-4", children: [profile && (_jsx(Button, { type: "button", basic: true, onClick: handleCancel, children: "Cancel" })), _jsx(AsyncButton, { primary: true, fluid: !profile, disabled: !isDirty ||
                                        !form.account_name ||
                                        !form.account_number ||
                                        !!accountNumberValidationError ||
                                        !form.bsb ||
                                        !form.abn ||
                                        !((_d = form.full_address) === null || _d === void 0 ? void 0 : _d.state) ||
                                        abnBlocksSave ||
                                        bsbBlocksSave, onClick: handleSave, children: !profile ? "Create" : updateLabel })] })] })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "grid grid-cols-1 gap-x-6 gap-y-8 md:grid-cols-2", children: [_jsx(ReadOnlyField, { label: "Account Name", value: form.account_name }), _jsx(ReadOnlyField, { label: "Account Number", value: form.account_number }), _jsx(ReadOnlyField, { label: "BSB", value: form.bsb }), _jsx(ReadOnlyField, { label: "GST Registered", value: form.gst_registered ? "Yes" : "No" }), _jsx(ReadOnlyField, { label: "ABN", value: form.abn }), _jsx(ReadOnlyField, { label: "Type", value: form.type }), _jsx(ReadOnlyField, { label: "Entity", value: form.entity }), _jsx(ReadOnlyField, { label: "Address", value: (_e = form.full_address) === null || _e === void 0 ? void 0 : _e.formatted, className: "md:col-span-2" })] }), _jsxs("div", { className: "sticky bottom-0 z-10 mt-6 flex flex-wrap items-center justify-end gap-2 border-t border-border bg-white pt-4", children: [syncActions && sapSyncEnabled && (_jsx(ProfileSyncActions, { row: doctor, syncActions: syncActions })), _jsx(Button, { primary: true, type: "button", onClick: () => setIsEditing(true), children: editLabel })] })] })) })] }));
};
